import { Button } from "@chakra-ui/button";
import { Skeleton } from "@chakra-ui/skeleton";
import {
  Box,
  Heading,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/layout";
import type { ISubscriptionPlansListQuery } from "../../../lib/api/sdk";
import { useSubscriptionPlansListQuery } from "../../../lib/api/sdk";
import type { ComponentProps } from "react";
import { useEffect, useMemo } from "react";
import type { FormStepProps } from "../interfaces";
import { IfAdd } from "../../../lib/conditional-expanders/conditionalExpanders";
import { FormWrapper } from "../Components/LayoutComponents";
import { INPUT_SPACING } from "../RegisterPage";
import { useErrorToast } from "../../../lib/utils/toasts/toasts";
import { useTranslations } from "next-intl";
import { groupBy } from "lodash";
import type { SportsTranslations, SubscriptionPlansTranslations } from "src/contexts/LocalesContext";
import { useLocales } from "src/contexts/LocalesContext";
import useTranslationConstants from "lib/utils/constants";
import RegisterHeading from "../Components/RegisterHeading";
import { useMediaQuery } from "@chakra-ui/react";

interface ISubscriptionPlanCardProps extends ComponentProps<any> {
  subscription_plan?: ISubscriptionPlansListQuery["subscription_plan"][0];
  loading?: boolean;
  isSelected?: boolean;
}

const SubscriptionPlanCard = ({
  subscription_plan,
  loading,
  isSelected,
  onClick,
}: ISubscriptionPlanCardProps) => {
  const t = useTranslations("pages.register");
  const { getTranslations } = useLocales();
  const { getSubscriptionPlanName } = useTranslationConstants();
  const subsPlanTranslations = getTranslations<SubscriptionPlansTranslations>(
    subscription_plan?.subscription_plan_translations as SubscriptionPlansTranslations[]
  );

  const spName = subscription_plan?.level ? getSubscriptionPlanName(subscription_plan?.level) : "";

  return (
    <Box
      flex="1"
      cursor={"pointer"}
      onClick={onClick}
      overflow="hidden"
      shadow="base"
      _hover={{ shadow: "md" }}
      borderColor={isSelected ? "brandOrange.100" : "brandOrange.500"}
      bg={isSelected ? "brandOrange.50" : "lightestGray"}
    >
      <VStack
        p={6}
        w="full"
        spacing={6}
        justify="flex-start"
        align="stretch"
        flex="1"
        h="full"
      >
        <Heading fontSize="lg">
          {spName}
        </Heading>
        <Box
          w="full"
          display="flex"
          flexDirection="row"
          alignItems="start"
          gap={INPUT_SPACING}
        >
          <VStack flex={1} align="start" spacing={2}>
            <Heading
              fontSize="4xl"
              color="brandOrange.500"
            >
              {subscription_plan?.weekly_price}€
            </Heading>
            <Text fontSize="xs" fontWeight="bold">
              {t("perWeek")}
            </Text>
          </VStack>
          <VStack flex={1} align="start" spacing={2}>
            <Heading
              fontSize="4xl"
              color="brandOrange.500"
            >
              {Number(subscription_plan?.weekly_price) * 4}€
            </Heading>
            <Text fontSize="xs" fontWeight="bold">
              {t("per4Weeks")}
            </Text>
          </VStack>
        </Box>

        <Skeleton isLoaded={!loading}>
          <Text noOfLines={5} fontSize="xs" fontWeight="bold">
            {subsPlanTranslations?.description}
          </Text>
        </Skeleton>
        <Button
          w="fit-content"
          minW="160px"
          colorScheme="brandOrange"
          variant="solid"
        >
          {isSelected ? t("selectPlan") : t("choose")}
        </Button>
      </VStack>
    </Box>
  );
};

const StepFour = ({
  FormController,
  onSubmit,
  goBack,
}: FormStepProps) => {
  const t = useTranslations("pages.register");
  const errorToast = useErrorToast();
  const { getTranslations } = useLocales();
  const coach_id = FormController.watch("coach_id");
  const sport_id = FormController.watch("sport_id");
  const plan_id = FormController.watch("plan_id");

  const [isTooSmall] = useMediaQuery("(max-width: 1300px)");

  const SubscriptionPlanListReq = useSubscriptionPlansListQuery({
    variables: {
      where: {
        sport_id: { _eq: sport_id },
        ...IfAdd(!!coach_id, {
          coach_id: { _eq: coach_id },
        }),
      },
    },
  });

  const groupedPlans = useMemo(() => {
    return groupBy(
      SubscriptionPlanListReq.data?.subscription_plan,
      "coach.id"
    );
  }, [SubscriptionPlanListReq.data?.subscription_plan]);

  const sportTranslations = getTranslations<SportsTranslations>(
    SubscriptionPlanListReq.data?.subscription_plan[0]?.sport?.sport_translations as SportsTranslations[]
  );

  useEffect(() => {
    if (Object.keys(FormController.formState.errors).length) {
      let error = "";
      Object.keys(FormController.formState.errors).map(
        key => (error += FormController.formState.errors[key].message)
      );
      errorToast({ error: new Error(error) });
    }
  }, [FormController.formState.errors]);

  return (
    <FormWrapper>
      <RegisterHeading
        color="brandOrange.500"
        fontSize="4xl"
      >
        {t("trainingPlans")}
      </RegisterHeading>
      <Text fontWeight="bold">
        {t("personalGoalsMessage")}
      </Text>

      <VStack align="start" w="full">
        <Heading fontSize="2xl">
          {sportTranslations?.title}
        </Heading>

        {SubscriptionPlanListReq.loading ? (
          <SimpleGrid columns={3} spacing={5} w="full">
            {[0, 1, 2].map(i => (
              <SubscriptionPlanCard key={i} loading />
            ))}
          </SimpleGrid>
        ) : !SubscriptionPlanListReq.data?.subscription_plan?.length ? (
          <Text fontSize="lg">{t("noPlansToShow")}</Text>
        ) : Object.entries(groupedPlans).map(([coach_id, plans], i) => (
          <VStack
            key={coach_id}
            w="full"
            spacing={INPUT_SPACING}
            align="stretch"
            pt={i !== 0 ? "8" : undefined}
          >
            <Text fontSize="md" mb="2">
              {t("plansFrom")}
              {plans[0]?.coach?.name}
            </Text>
            <SimpleGrid
              columns={[1, 1, 2, isTooSmall ? 2 : 3]}
              spacing={+INPUT_SPACING * 2}
              w="full"
            >
              {plans.map(plan => (
                <SubscriptionPlanCard
                  key={plan.id}
                  subscription_plan={plan}
                  isSelected={Number(plan_id) === plan.id}
                  onClick={() => {
                    FormController.setValue("plan_id", plan.id);
                    FormController.setValue("coach_id", coach_id);
                    setTimeout(() => {
                      FormController.handleSubmit(onSubmit)();
                    }, 500);
                  }}
                />
              ))}
            </SimpleGrid>
          </VStack>
        ))}
      </VStack>

      <VStack spacing={5} align="start" w="full" mt="6">
        <Button
          colorScheme={plan_id ? "brandOrange" : "gray"}
          onClick={FormController.handleSubmit(onSubmit)}
          isDisabled={!plan_id}
        >
          {t("nextButton2")}
        </Button>

        <Button colorScheme="gray" variant="link" onClick={goBack}>
          {t("backAthleteButton3")}
        </Button>
      </VStack>
    </FormWrapper>
  );
};

export default StepFour;
